import { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Integration, ZendeskIntegration } from '@/shared/types/integration';
import { getDocsUrl, getZendeskAuthUrl } from '@/util/constants';

import ApiClientFieldset from '../fieldsets/ApiClientFieldset';
import AuthorizationFieldset from '../fieldsets/AuthorizationFieldset';

interface Props {
  isUpdated: boolean;
  isUpdating: boolean;
  integration: ZendeskIntegration;
  updateIntegration?: (integration: ZendeskIntegration) => void;
}

const createOAuthURL = (integration: ZendeskIntegration) => {
  const queryParams = new URLSearchParams({
    redirect_uri: integration?.config?.redirect_uri ?? '',
    subdomain: integration?.config?.subdomain ?? '',
    client_id: integration?.config?.client_id ?? '',
    response_type: 'code',
    scope: 'read write chat',
  });

  return `${getZendeskAuthUrl(integration?.config?.subdomain)}?${queryParams}`;
};

export default function ZendeskOauth({
  isUpdated,
  isUpdating,
  integration,
  updateIntegration,
}: Props) {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);

  const [shouldRedirect, setShouldRedirect] = useState(false);

  const createCallbackURL = useCallback(
    (integration: Integration) => {
      const { desk_id, integration_id } = integration;
      return `${window?.location?.origin}/www/api/integrations/zendesk/${slug}/environments/${desk_id}/integrations/${integration_id}/callback`;
    },
    [slug]
  );

  const zendeskUrl = createOAuthURL(integration);
  const callbackUrl = createCallbackURL(integration);

  useEffect(() => {
    if (shouldRedirect && isUpdated && !isUpdating) {
      window.open(zendeskUrl, '_self');
      setShouldRedirect(false);
    }
  }, [callbackUrl, zendeskUrl, shouldRedirect, isUpdated, isUpdating]);

  return (
    <div className="flex flex-col gap-4">
      <ContextualHelp
        name="zendesk-setup"
        title={t('integrations.zendesk.name')}
        links={[
          {
            label: t('integrations.zendesk.help_label'),
            url: getDocsUrl('/docs/integrations/zendesk-chat'),
          },
        ]}
      >
        {t('integrations.zendesk.help_text')}
      </ContextualHelp>
      <ApiClientFieldset callbackUrl={callbackUrl} />
      <AuthorizationFieldset
        callbackUrl={callbackUrl}
        isUpdated={isUpdated}
        isUpdating={isUpdating}
        integration={integration}
        updateIntegration={updateIntegration}
      />
    </div>
  );
}
